import { cn } from '@sjuni/ui';
import { Check } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';

import type { TagRow } from './types';

/**
 * docs/03-DATA-MODEL.md §4's `news_tag` pivot, as a field.
 *
 * Toggle buttons rather than a `<select multiple>`, which is unusable on touch
 * and unreadable on desktop, and rather than a combobox, which would be a lot
 * of machinery for a list that the migration will hold a few hundred of and a
 * filter box handles.
 *
 * Selected tags stay at the top of the list. With three hundred imported tags
 * the alternative is choosing four and then losing them among the rest, with no
 * way to see what an article carries without scrolling.
 */
export interface TagPickerProps {
  tags: TagRow[];
  selected: number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
}

export function TagPicker({ tags, selected, onChange, disabled }: TagPickerProps) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const groupId = useId();

  const [filter, setFilter] = useState('');

  const needle = filter.trim().toLocaleLowerCase();

  const visible = tags
    .filter(
      (tag) =>
        needle === '' ||
        selected.includes(tag.id) ||
        valueFor(tag.name, locale).toLocaleLowerCase().includes(needle),
    )
    .sort((a, b) => Number(selected.includes(b.id)) - Number(selected.includes(a.id)));

  return (
    <fieldset>
      <legend className="text-body-sm font-semibold text-primary">{t('news.tags.label')}</legend>
      <p className="mt-0.5 text-caption text-muted">{t('news.tags.hint')}</p>

      {tags.length > 12 ? (
        <input
          type="search"
          aria-label={t('news.tags.filter')}
          placeholder={t('news.tags.filter')}
          value={filter}
          disabled={disabled}
          onChange={(event) => setFilter(event.target.value)}
          className="mt-2 w-full rounded-full border border-subtle bg-surface px-4 py-2 text-body-sm text-primary placeholder:text-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
        />
      ) : null}

      <div id={groupId} className="mt-2 flex flex-wrap gap-2">
        {visible.length === 0 ? (
          <p className="text-caption text-muted">{t('news.tags.none')}</p>
        ) : (
          visible.map((tag) => {
            const on = selected.includes(tag.id);

            return (
              <button
                key={tag.id}
                type="button"
                // `aria-pressed` rather than a checkbox role: this is a toggle
                // button, and the state is what a screen reader has to hear.
                aria-pressed={on}
                disabled={disabled}
                onClick={() =>
                  onChange(on ? selected.filter((id) => id !== tag.id) : [...selected, tag.id])
                }
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-caption font-semibold transition-colors duration-fast',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600',
                  'disabled:pointer-events-none disabled:opacity-40',
                  on
                    ? 'border-navy-600 bg-navy-600 text-white'
                    : 'border-subtle text-secondary hover:text-primary',
                )}
              >
                {on ? <Check size={12} strokeWidth={2.5} aria-hidden="true" /> : null}
                {valueFor(tag.name, locale)}
              </button>
            );
          })
        )}
      </div>
    </fieldset>
  );
}
